import React, { useState, useEffect, useCallback, useMemo } from 'react';
import NumericTextField from './NumericTextField';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { DOCUMENT, documentAddButtonSx, documentEyebrowSx, documentTextButtonSx, getDocumentInitials } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';
import { isManagedByPartner, isGroupTrip } from '../utils/tripUtils';

import { CURRENCIES } from '../constants/currencies';

const EXPENSE_CATEGORIES = [
  { value: 'accommodation', label: 'Accommodation', icon: '🏨' },
  { value: 'transport', label: 'Transportation', icon: '🚗' },
  { value: 'food', label: 'Food & Dining', icon: '🍽️' },
  { value: 'entertainment', label: 'Entertainment', icon: '🎭' },
  { value: 'shopping', label: 'Shopping', icon: '🛍️' },
  { value: 'health', label: 'Health & Medical', icon: '🏥' },
  { value: 'other', label: 'Other', icon: '📝' },
];

const parseSharedMembers = (value) => {
  if (Array.isArray(value)) return value.map(Number).filter((id) => Number.isFinite(id));
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(Number).filter((id) => Number.isFinite(id)) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const memberLabel = (member) => {
  const name = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
  return name || member.username || member.email || String(member.id);
};

const TripExpenses = ({ tripId, trip, isPartner = false, assignableMembers: assignableMembersProp }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const requestConfig = useMemo(() => (isPartner ? { usePartnerAuth: true } : null), [isPartner]);
  const apiGet = useCallback((url) => (requestConfig ? api.get(url, requestConfig) : api.get(url)), [requestConfig]);
  const apiPost = useCallback((url, data) => (requestConfig ? api.post(url, data, requestConfig) : api.post(url, data)), [requestConfig]);
  const apiPut = useCallback((url, data) => (requestConfig ? api.put(url, data, requestConfig) : api.put(url, data)), [requestConfig]);
  const apiDelete = useCallback((url) => (requestConfig ? api.delete(url, requestConfig) : api.delete(url)), [requestConfig]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [error, setError] = useState('');
  const [settlingKey, setSettlingKey] = useState(null);

  const tripPrivateMode = isManagedByPartner(trip) || isGroupTrip(trip);
  const privateMode = Boolean(summary?.privateMode ?? tripPrivateMode);

  const assignableMembers = useMemo(() => {
    if (Array.isArray(assignableMembersProp) && assignableMembersProp.length) {
      return assignableMembersProp;
    }
    if (Array.isArray(summary?.assignableMembers)) return summary.assignableMembers;
    const fromTrip = (trip?.members || trip?.all_participants || [])
      .filter((m) => m.status === 'creator' || m.status === 'member' || !m.status)
      .map((m) => ({
        id: Number(m.id || m.user_id),
        username: m.username,
        email: m.email,
        first_name: m.first_name,
        last_name: m.last_name,
      }))
      .filter((m) => Number.isFinite(m.id));
    return fromTrip;
  }, [assignableMembersProp, summary?.assignableMembers, trip]);

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    currency: 'USD',
    category: 'other',
    shared_with_all: true,
    shared_with_members: [],
    receipt_url: '',
  });

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const [expensesRes, summaryRes] = await Promise.all([
        apiGet(`/trips/${tripId}/expenses`),
        apiGet(`/trips/${tripId}/expenses/summary`),
      ]);

      setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : (expensesRes.data?.expenses || []));
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching expenses:', err);
      setError('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  }, [tripId, apiGet]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleMarkAsPaid = async (settlement) => {
    const key = `${settlement.from_user_id}-${settlement.to_user_id}`;
    setSettlingKey(key);
    setError('');

    try {
      await apiPost(`/trips/${tripId}/settlements`, {
        fromUserId: settlement.from_user_id,
        toUserId: settlement.to_user_id,
        amount: Number(settlement.amount),
        currency: summary?.currency || 'USD',
        method: 'other'
      });
      await fetchExpenses();
    } catch (err) {
      setError(err.response?.data?.error || t('expenses.settleUpFailed'));
    } finally {
      setSettlingKey(null);
    }
  };

  const handleUndoSettlement = async (settlementId) => {
    setError('');
    try {
      await apiDelete(`/trips/${tripId}/settlements/${settlementId}`);
      await fetchExpenses();
    } catch (err) {
      setError(err.response?.data?.error || t('expenses.settleUpFailed'));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.description || !formData.amount) {
        setError(t('expenses.descriptionAmountRequired'));
        return;
      }

      if ((privateMode || isPartner) && (!formData.shared_with_members || formData.shared_with_members.length === 0) && isPartner) {
        setError(t('expenses.assignRequired'));
        return;
      }

      const expenseData = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        category: formData.category,
        receipt_url: formData.receipt_url,
        shared_with_all: privateMode || isPartner ? false : formData.shared_with_all,
        shared_with_members: (privateMode || isPartner || !formData.shared_with_all)
          ? formData.shared_with_members
          : null,
      };

      if (selectedExpense) {
        await apiPut(`/trips/${tripId}/expenses/${selectedExpense.id}`, expenseData);
      } else {
        await apiPost(`/trips/${tripId}/expenses`, expenseData);
      }

      await fetchExpenses();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving expense:', err);
      setError(err.response?.data?.error || t('expenses.saveError'));
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm(t('expenses.confirmDelete'))) return;

    try {
      await apiDelete(`/trips/${tripId}/expenses/${expenseId}`);
      await fetchExpenses();
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError(t('expenses.deleteError'));
    }
  };

  const handleOpenAddDialog = () => {
    setSelectedExpense(null);
    setFormData({
      description: '',
      amount: '',
      currency: 'USD',
      category: 'other',
      shared_with_all: !(privateMode || isPartner),
      shared_with_members: [],
      receipt_url: '',
    });
    setAddDialogOpen(true);
    setError('');
  };

  const handleOpenEditDialog = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount.toString(),
      currency: expense.currency,
      category: expense.category,
      shared_with_all: Boolean(expense.shared_with_all),
      shared_with_members: parseSharedMembers(expense.shared_with_members),
      receipt_url: expense.receipt_url || '',
    });
    setEditDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setSelectedExpense(null);
    setError('');
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const currencyInfo = CURRENCIES.find(c => c.code === currency);
    const numericAmount = Number(amount) || 0;
    return `${currencyInfo?.symbol || '$'}${numericAmount.toFixed(2)}`;
  };

  const getCurrencySymbol = () => {
    if (summary?.currency) {
      const currencyInfo = CURRENCIES.find(c => c.code === summary.currency);
      return currencyInfo?.symbol || '$';
    }
    return '$';
  };

  const getCategoryInfo = (category) => {
    return EXPENSE_CATEGORIES.find(c => c.value === category) || EXPENSE_CATEGORIES.find(c => c.value === 'other');
  };

  const canEditExpense = (expense) => {
    if (isPartner) return true;
    return Number(expense.payer_id) === Number(user?.id)
      || Number(expense.created_by) === Number(user?.id);
  };

  const shareableMembersForPicker = useMemo(() => {
    if (isPartner) return assignableMembers;
    return assignableMembers.filter((m) => Number(m.id) !== Number(user?.id));
  }, [assignableMembers, isPartner, user?.id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DocumentSectionHead
        title={`${t('expenses.title')} (${expenses.length})`}
        meta={t('expenses.entries', { count: expenses.length })}
      />

      {summary && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: '1px',
            bgcolor: DOCUMENT.ruleHard,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            mb: 3.5,
          }}
        >
          {[
            { label: t('expenses.spent'), value: `${getCurrencySymbol()}${(Number(summary.totalExpenses) || 0).toFixed(2)}` },
            { label: t('expenses.budget'), value: trip?.budget ? `${getCurrencySymbol()}${Number(trip.budget).toFixed(2)}` : '—' },
            {
              label: t('expenses.remaining'),
              value: trip?.budget
                ? `${getCurrencySymbol()}${Math.max(0, Number(trip.budget) - (Number(summary.totalExpenses) || 0)).toFixed(2)}`
                : '—',
            },
            {
              label: t('expenses.youAreOwed'),
              value: `${getCurrencySymbol()}${Math.max(0, Number((summary.memberBalances || {})[user?.id]) || 0).toFixed(2)}`,
            },
          ].map((cell) => (
            <Box key={cell.label} sx={{ bgcolor: DOCUMENT.canvas, px: 2, py: 1.75 }}>
              <Typography component="dt" sx={documentEyebrowSx}>{cell.label}</Typography>
              <Typography
                component="dd"
                sx={{ fontSize: 22, fontFamily: DOCUMENT.display, mt: 0.75, m: 0, color: DOCUMENT.ink }}
              >
                {cell.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {!isPartner && summary && (
        <Box sx={{ mb: 3.5 }}>
          <DocumentSectionHead title={t('expenses.memberBalances')} sx={{ mb: 1.5 }} />
          {Object.entries(summary.memberTotals || {}).map(([memberId, totals]) => {
            const balance = Number((summary.memberBalances || {})[memberId]) || 0;
            const isPositive = balance > 0.01;
            const isNegative = balance < -0.01;
            const amount = `${getCurrencySymbol()}${Math.abs(balance).toFixed(2)}`;
            return (
              <Box
                key={memberId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  py: 1.75,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: DOCUMENT.oxblood,
                    color: DOCUMENT.ivory,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {getDocumentInitials(totals.username || 'U')}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: 15 }}>{totals.username || 'Unknown User'}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                    {t('expenses.paid')}: {getCurrencySymbol()}{(Number(totals.paid) || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', color: isPositive ? DOCUMENT.accent : DOCUMENT.ink2 }}>
                  {isPositive ? t('expenses.owedAmount', { amount }) : isNegative ? t('expenses.owesAmount', { amount }) : t('expenses.even')}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {!isPartner && summary && (summary.settlements || []).length > 0 && (
        <Box sx={{ mb: 3.5 }}>
          <DocumentSectionHead title={t('expenses.settlementSuggestions')} sx={{ mb: 1.5 }} />
          {(summary.settlements || []).map((settlement, index) => {
            const settlementKey = `${settlement.from_user_id}-${settlement.to_user_id}`;
            const isParticipant = settlement.from_user_id === user?.id || settlement.to_user_id === user?.id;
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1.6,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  fontSize: 14,
                }}
              >
                <Typography>
                  {t('expenses.owesArrow', {
                    from: settlement.from_username || 'Unknown',
                    to: settlement.to_username || 'Unknown',
                    amount: `${getCurrencySymbol()}${(Number(settlement.amount) || 0).toFixed(2)}`,
                  })}
                </Typography>
                <Button
                  size="small"
                  data-testid="mark-as-paid"
                  disabled={settlingKey === settlementKey}
                  onClick={() => handleMarkAsPaid(settlement)}
                  sx={{
                    ...documentTextButtonSx,
                    flexShrink: 0,
                    fontWeight: isParticipant ? 600 : 400,
                  }}
                >
                  {settlingKey === settlementKey ? t('expenses.recording') : t('expenses.markAsPaid')}
                </Button>
              </Box>
            );
          })}
        </Box>
      )}

      {!isPartner && summary && (summary.settlementHistory || []).length > 0 && (
        <Box sx={{ mb: 3.5 }}>
          <DocumentSectionHead title={t('expenses.settlementHistory')} sx={{ mb: 1.5 }} />
          {summary.settlementHistory.map((record) => (
            <Box
              key={record.id}
              data-testid="settlement-record"
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2,
                py: 1.6,
                borderBottom: `1px solid ${DOCUMENT.rule}`,
                fontSize: 14,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14 }}>
                  {t('expenses.paidArrow', {
                    from: record.from_username || 'Unknown',
                    to: record.to_username || 'Unknown',
                    amount: formatCurrency(record.amount, record.currency),
                  })}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                  {new Date(record.settled_at).toLocaleDateString()}
                  {record.note ? ` · ${record.note}` : ''}
                </Typography>
              </Box>
              {(record.recorded_by === user?.id) && (
                <Button
                  size="small"
                  onClick={() => handleUndoSettlement(record.id)}
                  sx={{ ...documentTextButtonSx, flexShrink: 0 }}
                >
                  {t('expenses.undoSettlement')}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!isPartner && summary && (summary.settlements || []).length === 0 && expenses.length > 0 && (
        <Typography sx={{ mb: 3, color: DOCUMENT.ink3, fontSize: 14 }}>
          {t('expenses.allSettled')}
        </Typography>
      )}

      {error && !addDialogOpen && !editDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {expenses.length === 0 ? (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 1 }}>
            {t('expenses.noExpenses')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, mb: 2, fontSize: 14 }}>
            {isPartner ? t('expenses.noExpensesHintPartner') : t('expenses.noExpensesHint')}
          </Typography>
          <Button onClick={handleOpenAddDialog} sx={documentAddButtonSx} data-testid="expenses-add-first">
            {t('expenses.addFirstExpense')}
          </Button>
        </Box>
      ) : (
        <Box>
          {expenses.map((expense) => {
            const categoryInfo = getCategoryInfo(expense.category);
            const canEdit = canEditExpense(expense);

            return (
              <Box
                key={expense.id}
                data-testid="expense-card"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: '1.4fr 0.7fr 0.7fr 0.6fr auto' },
                  gap: 1.5,
                  py: 1.6,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  fontSize: 14,
                  alignItems: 'baseline',
                }}
              >
                <Typography>{expense.description}</Typography>
                <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                  {categoryInfo.label}
                </Typography>
                <Typography sx={{ color: DOCUMENT.ink2 }}>{expense.payer_name}</Typography>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums', textAlign: { md: 'right' } }}>
                  {formatCurrency(expense.amount, expense.currency)}
                </Typography>
                {canEdit && (
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={() => handleOpenEditDialog(expense)}
                      sx={documentTextButtonSx}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleDelete(expense.id)}
                      sx={{ ...documentTextButtonSx, '&:hover': { color: '#8C2B22', borderBottom: '1px solid #8C2B22' } }}
                    >
                      {t('common.delete')}
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
          <Button onClick={handleOpenAddDialog} sx={documentAddButtonSx} data-testid="expenses-add">
            {t('expenses.addExpense')}
          </Button>
        </Box>
      )}

      {(addDialogOpen || editDialogOpen) && (
      <Dialog
        open
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        transitionDuration={0}
        disableScrollLock
      >
        <DialogTitle>
          {selectedExpense ? t('expenses.editExpense') : t('expenses.addNewExpense')}
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label={t('expenses.description')}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            required
          />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={8}>
              <NumericTextField
                fullWidth
                name="amount"
                label={t('expenses.amount')}
                value={formData.amount}
                onValueChange={(value) => setFormData({ ...formData, amount: value })}
                allowDecimal
                allowEmpty
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {CURRENCIES.find(c => c.code === formData.currency)?.symbol || '$'}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="expense-currency-label" shrink htmlFor="expense-currency">{t('expenses.currency')}</InputLabel>
                <Select
                  native
                  id="expense-currency"
                  labelId="expense-currency-label"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  label={t('expenses.currency')}
                  inputProps={{ 'aria-label': t('expenses.currency') }}
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <FormControl fullWidth margin="normal">
            <InputLabel>{t('expenses.category')}</InputLabel>
            <Select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              label={t('expenses.category')}
            >
              {EXPENSE_CATEGORIES.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.icon} {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(privateMode || isPartner) ? (
            <FormControl fullWidth margin="normal" data-testid="expense-share-picker">
              <InputLabel id="expense-share-label">
                {isPartner ? t('expenses.assignTo') : t('expenses.shareWith')}
              </InputLabel>
              <Select
                multiple
                labelId="expense-share-label"
                value={formData.shared_with_members}
                onChange={(e) => setFormData({
                  ...formData,
                  shared_with_members: e.target.value.map(Number),
                  shared_with_all: false,
                })}
                input={<OutlinedInput label={isPartner ? t('expenses.assignTo') : t('expenses.shareWith')} />}
                renderValue={(selected) => selected
                  .map((id) => {
                    const member = shareableMembersForPicker.find((m) => Number(m.id) === Number(id));
                    return member ? memberLabel(member) : id;
                  })
                  .join(', ')}
              >
                {shareableMembersForPicker.map((member) => (
                  <MenuItem key={member.id} value={Number(member.id)}>
                    <Checkbox checked={formData.shared_with_members.map(Number).includes(Number(member.id))} />
                    <ListItemText primary={memberLabel(member)} secondary={member.email} />
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ mt: 1, fontSize: 12.5, color: DOCUMENT.ink3 }}>
                {isPartner ? t('expenses.assignHint') : t('expenses.privateShareHint')}
              </Typography>
            </FormControl>
          ) : (
            <FormControl component="fieldset" margin="normal">
              <FormLabel component="legend">{t('expenses.expenseSharing')}</FormLabel>
              <RadioGroup
                value={formData.shared_with_all ? 'all' : 'specific'}
                onChange={(e) => setFormData({
                  ...formData,
                  shared_with_all: e.target.value === 'all',
                  shared_with_members: e.target.value === 'all' ? [] : formData.shared_with_members
                })}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={t('expenses.shareWithAll')}
                />
                <FormControlLabel
                  value="specific"
                  control={<Radio />}
                  label={t('expenses.personalExpense')}
                />
              </RadioGroup>
            </FormControl>
          )}

          <TextField
            fullWidth
            label={t('expenses.receiptUrl')}
            value={formData.receipt_url}
            onChange={(e) => setFormData({ ...formData, receipt_url: e.target.value })}
            margin="normal"
            placeholder="https://example.com/receipt.jpg"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            data-testid="expenses-save"
            sx={{
              bgcolor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              borderRadius: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.09em',
              boxShadow: 'none',
              '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
            }}
          >
            {selectedExpense ? t('expenses.updateExpense') : t('expenses.addExpense')}
          </Button>
        </DialogActions>
      </Dialog>
      )}
    </Box>
  );
};

export default TripExpenses;
